import React, { useEffect, useRef, useState } from "react";


function drawGauge(ctx, { W, H, value, maxValue, referenceValue, referenceLabel }) {
  const scale = W / 460;
  const cx = W / 2;
  const cy = 175 * scale;
  const r = 108 * scale;
  const aw = 26 * scale;
  const inner = r - aw / 2;
  const outer = r + aw / 2;

  ctx.clearRect(0, 0, W, H);

  [
    ["#CC0000", 0,     0.167],
    ["#EE3322", 0.167, 0.333],
    ["#FF8800", 0.333, 0.5  ],
    ["#FFD700", 0.5,   0.667],
    ["#AACC00", 0.667, 0.833],
    ["#33AA00", 0.833, 1.0  ],
  ].forEach(([color, from, to]) => {
    ctx.beginPath();
    ctx.arc(cx, cy, r, Math.PI + from * Math.PI, Math.PI + to * Math.PI);
    ctx.lineWidth = aw;
    ctx.strokeStyle = color;
    ctx.lineCap = "butt";
    ctx.stroke();
  });

  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 2.5;
  ctx.setLineDash([]);
  [0.167, 0.333, 0.5, 0.667, 0.833].forEach((pct) => {
    const a = Math.PI + pct * Math.PI;
    ctx.beginPath();
    ctx.moveTo(cx + inner * Math.cos(a), cy + inner * Math.sin(a));
    ctx.lineTo(cx + outer * Math.cos(a), cy + outer * Math.sin(a));
    ctx.stroke();
  });

  ctx.fillStyle = "#888";
  ctx.font = `${10 * scale}px Arial`;
  ctx.textBaseline = "middle";
  [
    [0,    "0,0%",   "right" ],
    [0.25, "25,0%",  "right" ],
    [0.5,  "50,0%",  "center"],
    [0.75, "75,0%",  "left"  ],
    [1.0,  "100,0%", "left"  ],
  ].forEach(([pct, label, align]) => {
    const a = Math.PI + pct * Math.PI;
    ctx.textAlign = align;
    ctx.fillText(label, cx + (outer + 14 * scale) * Math.cos(a), cy + (outer + 14 * scale) * Math.sin(a));
  });

  if (referenceValue != null) {
    const refPct = referenceValue / maxValue;
    const refAngle = Math.PI + refPct * Math.PI;

    ctx.beginPath();
    ctx.moveTo(cx + inner * Math.cos(refAngle), cy + inner * Math.sin(refAngle));
    ctx.lineTo(cx + outer * Math.cos(refAngle), cy + outer * Math.sin(refAngle));
    ctx.strokeStyle = "#1A2B49";
    ctx.lineWidth = 3 * scale;
    ctx.stroke();

    const lex = cx + (outer + 32 * scale) * Math.cos(refAngle);
    const ley = cy + (outer + 32 * scale) * Math.sin(refAngle);

    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(lex, ley);
    ctx.strokeStyle = "#999";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.stroke();
    ctx.setLineDash([]);

    const isRight = refPct >= 0.5;
    const tlx = isRight ? Math.min(lex + 5 * scale, W - 40 * scale) : lex - 5 * scale;
    ctx.fillStyle = "#1A2B49";
    ctx.font = `bold ${10 * scale}px Arial`;
    ctx.textBaseline = "middle";
    ctx.textAlign = isRight ? "left" : "right";
    ctx.fillText(`${referenceLabel}:`, tlx, ley - 7 * scale);
    ctx.fillText(`${String(referenceValue.toFixed(1)).replace(".", ",")}%`, tlx, ley + 7 * scale);
  }

  const pct = Math.min(Math.max(value / maxValue, 0), 1);
  const needleAngle = Math.PI + pct * Math.PI;
  const tipX = cx + (inner - 4 * scale) * Math.cos(needleAngle);
  const tipY = cy + (inner - 4 * scale) * Math.sin(needleAngle);
  const perpAngle = needleAngle + Math.PI / 2;
  const baseW = 6 * scale;

  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(cx + baseW * Math.cos(perpAngle), cy + baseW * Math.sin(perpAngle));
  ctx.lineTo(cx - baseW * Math.cos(perpAngle), cy - baseW * Math.sin(perpAngle));
  ctx.closePath();
  ctx.fillStyle = "#1A2B49";
  ctx.fill();

  ctx.beginPath();
  ctx.arc(cx, cy, 8 * scale, 0, 2 * Math.PI);
  ctx.fill();
}

const GaugeChart = React.memo(function GaugeChart({ value, maxValue = 100, referenceValue, referenceLabel, valueLabel }) {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const [size, setSize] = useState({ W: 460, H: 240 });

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0].contentRect.width;
      const W = Math.max(160, Math.min(cw, 460));
      const H = Math.round((W / 460) * 240);
      setSize((prev) => (prev.W === W && prev.H === H ? prev : { W, H }));
    });
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size.W * dpr;
    canvas.height = size.H * dpr;
    canvas.style.width = `${size.W}px`;
    canvas.style.height = `${size.H}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawGauge(ctx, { W: size.W, H: size.H, value, maxValue, referenceValue, referenceLabel });
  }, [size, value, maxValue, referenceValue, referenceLabel]);

  const scale = size.W / 460;
  const valueFontPx = Math.max(20, Math.round(34 * scale));
  const labelFontPx = Math.max(11, Math.round(13 * scale));

  return (
    <div ref={wrapperRef} className="flex flex-col items-center w-full">
      <canvas ref={canvasRef} />
      <div
        className="font-bold text-[#1A2B49]"
        style={{ marginTop: -28 * scale, fontSize: valueFontPx, lineHeight: 1 }}
      >
        {String(value.toFixed(1)).replace(".", ",")}%
      </div>
      <div className="text-[#01054C] font-semibold mt-1" style={{ fontSize: labelFontPx }}>
        {valueLabel}
      </div>
    </div>
  );
});

export default GaugeChart;
